import IconButton from '@material-ui/core/IconButton';
import classnames from 'classnames';
import { ReactElement, ReactNode, useRef } from 'react';
import { FileDrop } from 'react-file-drop';

import { isFile, validateFileType } from '~/utils/file';

import { FormHelperText } from '@material-ui/core';
import editIcon from '~/ui/assets/images/editGrey.svg';
import deleteIcon from '~/ui/assets/images/trashBlack.svg';
import imgSrc from '~/ui/assets/images/undraw.svg';
import { ACCEPTABLE_IMAGE_IMAGES_TYPES } from '~/ui/constants/common';

import styles from './AvatarUpload.module.scss';

interface IProps {
  value?: File | string;
  acceptTypes?: string;
  maxFileSize?: number;
  contentTitle?: string;
  className?: string;
  filePreview: ReactNode;
  onUpload: (file: File) => void;
  onRemove: () => void;
  onError: (err: string) => void;
  helperText?: string;
}

const AvatarUpload = ({
  value,
  acceptTypes = ACCEPTABLE_IMAGE_IMAGES_TYPES,
  maxFileSize = 2, // 2Mb
  contentTitle,
  className,
  filePreview,
  onUpload,
  onRemove,
  onError,
  helperText,
}: IProps): ReactElement => {
  const fileInputRef = useRef(null);

  const validateFile = (file: File) => {
    if (!isFile(file)) return 'No file';

    if (!validateFileType(file, acceptTypes)) {
      const shortTypeNames = acceptTypes
        .split(',')
        .map(type => type.split('/').pop().toLowerCase())
        .join(',');

      return `Only ${shortTypeNames} formats are valid`;
    }

    // convert fileSize from Mb to bytes (1 Mb = 1048576 Bytes)
    const maxFileSizeBytes = maxFileSize * 1048576;

    if (maxFileSize && file.size > maxFileSizeBytes) return `Max file size - ${maxFileSize}Mb`;

    return '';
  };

  const onTargetClick = () => {
    fileInputRef.current.click();
  };

  const onChange = (file: File) => {
    const err = validateFile(file);

    if (err) {
      onError(err);
    } else {
      // Reset input value
      fileInputRef.current.value = '';
      onUpload(file);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <div
          className={classnames(styles.dragAndDropContainer, { [styles.hidden]: Boolean(value) })}
        >
          <FileDrop
            onTargetClick={onTargetClick}
            onDrop={(files, event) => {
              event.preventDefault();
              onChange(files[0]);
            }}
            className={styles.dropzone}
            targetClassName={styles.dropzoneTarget}
            draggingOverTargetClassName={styles.draggingOverTargetClassName}
          >
            {null}
          </FileDrop>
          {/* Use input for onClick handler */}
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptTypes}
            className={styles.hidden}
            onChange={event => {
              const { files } = event.target;
              onChange(files[0]);
            }}
          />
          <div className={styles.dragAndDropDisplayContent}>
            <div className={styles.contentIcon}>
              <img src={imgSrc} alt="undraw" />
            </div>
            <div>
              <div className={styles.contentTitle}>Upload {contentTitle}</div>
              <div className={styles.contentDescription}>
                Drag and drop files or browse your computer
              </div>
            </div>
          </div>
        </div>
        {value && (
          <div className={classnames(styles.previewContainer, className)}>
            {filePreview}
            <div>
              <IconButton onClick={onTargetClick} title="Edit">
                <img src={editIcon} alt="edit" />
              </IconButton>
              <IconButton onClick={onRemove} title="Remove">
                <img src={deleteIcon} alt="delete" />
              </IconButton>
            </div>
          </div>
        )}
      </div>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </>
  );
};

export default AvatarUpload;
